const state = {
  records: [],
  meta: {
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 15,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  businessAccountByContactId: null,
};

const getters = {
  getBusinessAccountsList: state => state.records,
  getUIFlags: state => state.uiFlags,
  getMeta: state => state.meta,
  getBusinessAccountByContactId: state => state.businessAccountByContactId,
};

import BusinessAccountAPI from '../../api/businessAccounts';

const actions = {
  async get({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await BusinessAccountAPI.get(params);
      commit(
        'SET_BUSINESS_ACCOUNTS',
        response.data?.payload || response.data || []
      );
      if (response.data?.meta) {
        commit('SET_META', {
          currentPage: parseInt(response.data.meta.current_page),
          totalItems: parseInt(response.data.meta.total_items),
          itemsPerPage: parseInt(response.data.meta.items_per_page),
        });
      }
      return response.data?.payload || response.data || [];
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async create({ commit }, businessAccountData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await BusinessAccountAPI.create(businessAccountData);
      commit('ADD_BUSINESS_ACCOUNT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, businessAccountData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await BusinessAccountAPI.update(id, businessAccountData);
      commit('UPDATE_BUSINESS_ACCOUNT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async show({ commit }, id) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await BusinessAccountAPI.show(id);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async linkContacts(
    { commit },
    { id, contactIds, force = false, keepBoth = false, contactDecisions = null }
  ) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await BusinessAccountAPI.linkContacts(
        id,
        contactIds,
        force,
        keepBoth,
        contactDecisions
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async unlinkContact({ commit }, { id, contactId }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await BusinessAccountAPI.unlinkContact(id, contactId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await BusinessAccountAPI.delete(id);
      commit('DELETE_BUSINESS_ACCOUNT', id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async getBusinessAccountByContactId({ commit }, contactId) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response =
        await BusinessAccountAPI.getBusinessAccountByContactId(contactId);
      commit('SET_BUSINESS_ACCOUNT_BY_CONTACT_ID', response.data);
      return response.data;
    } catch (error) {
      commit('SET_BUSINESS_ACCOUNT_BY_CONTACT_ID', null);
      
      // Handle 404 as expected case (contact may not have a business account)
      if (error?.isAxiosError && error?.response?.status === 404) {
        return null;
      }
      
      // For other errors, throw with proper error message extraction
      const errorMessage = error?.message || error?.response?.data?.message || String(error);
      throw new Error(errorMessage);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },
};

const mutations = {
  SET_BUSINESS_ACCOUNTS(state, businessAccounts) {
    state.records = businessAccounts;
  },

  ADD_BUSINESS_ACCOUNT(state, businessAccount) {
    state.records.push(businessAccount);
  },

  UPDATE_BUSINESS_ACCOUNT(state, businessAccount) {
    const index = state.records.findIndex(
      account => account.id === businessAccount.id
    );
    if (index !== -1) {
      state.records.splice(index, 1, businessAccount);
    }
  },

  DELETE_BUSINESS_ACCOUNT(state, id) {
    state.records = state.records.filter(account => account.id !== id);
  },

  SET_META(state, meta) {
    state.meta = { ...state.meta, ...meta };
  },

  SET_UI_FLAG(state, { isFetching, isCreating, isUpdating, isDeleting }) {
    if (isFetching !== undefined) state.uiFlags.isFetching = isFetching;
    if (isCreating !== undefined) state.uiFlags.isCreating = isCreating;
    if (isUpdating !== undefined) state.uiFlags.isUpdating = isUpdating;
    if (isDeleting !== undefined) state.uiFlags.isDeleting = isDeleting;
  },

  SET_BUSINESS_ACCOUNT_BY_CONTACT_ID(state, businessAccount) {
    state.businessAccountByContactId = businessAccount;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
